import type { DoctorService, DoctorServiceExpanded } from "@hermes/api";
import { useMessage } from "@hermes/intl";
import { cssFns, usePrincipalColors } from "@hermes/ui";
import { type SerializedStaticStyles } from "@hermes/ui/src/css";

export const PatientAge = ({
  branchServices,
  styles,
}: {
  branchServices?: DoctorServiceExpanded[] | DoctorService[];
  styles?: SerializedStaticStyles;
}) => {
  const message = useMessage();
  const { gs4 } = usePrincipalColors();

  if (!branchServices) return;

  const hasAdult = branchServices.some((service) => !service.age_to);
  const hasChild = branchServices.some(
    (service) => service.age_to && service.age_to <= 216,
  );
  const childAges = branchServices
    .filter((s) => s.age_to && s.age_to <= 216)
    .map((s) => [s.age_from, s.age_to])
    .flat()
    .filter((age) => typeof age === "number");

  const minAge = childAges.length ? Math.min(...childAges) : 0;
  const maxAge = childAges.length ? Math.max(...childAges) : 216;

  const getAgeInfo = (min: number, max: number) => {
    const from = Math.floor(min > 12 ? min / 12 : min);
    const to = Math.floor(max > 12 ? max / 12 : max);
    const fromUnit = from > 12 ? "y" : "m";
    const toUnit = to > 12 ? "y" : "m";
    return { ageFrom: from, ageTo: to, fromUnit, toUnit };
  };

  const { ageFrom, ageTo, fromUnit, toUnit } = getAgeInfo(minAge, maxAge);

  const content =
    hasAdult && hasChild
      ? message({
          id: "common.age-info.adult-child",
          default:
            "Adults and children from {age_from} {age_from_unit, select, m {months} other {years}} to {age_to} {age_to_unit, select, m {months} other {years}}",
          values: {
            age_from: ageFrom,
            age_to: ageTo,
            age_from_unit: fromUnit,
            age_to_unit: toUnit,
          },
        })
      : hasAdult
        ? message({ id: "common.age-info.only-adult", default: "Adults only" })
        : hasChild
          ? message({
              id: "common.age-info.only-child",
              default:
                "Children from {age_from} {age_from_unit, select, m {months} other {years}} to {age_to} {age_to_unit, select, m {months} other {years}}",
              values: {
                age_from: ageFrom,
                age_to: ageTo,
                age_from_unit: fromUnit,
                age_to_unit: toUnit,
              },
            })
          : null;

  return (
    <div
      css={[
        styles,
        cssFns.typo({ level: "body-1", weight: "regular", color: gs4 }),
        { marginBlockEnd: 6 },
      ]}
    >
      {content}
    </div>
  );
};
